/**
 * Oblique Real-Time Notification & Call Sound Engine
 * Provides studio-quality synthesized chimes (notifications) and ringtones (calls/meetings) via Web Audio API.
 * 100% offline, zero network latency, zero broken audio assets.
 */

let audioCtxInstance = null;

const getAudioContext = () => {
    if (typeof window === 'undefined') return null;
    try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return null;
        if (!audioCtxInstance) {
            audioCtxInstance = new AudioCtx();
        }
        if (audioCtxInstance.state === 'suspended') {
            audioCtxInstance.resume().catch(() => {});
        }
        return audioCtxInstance;
    } catch {
        return null;
    }
};

export const NOTIFICATION_TONES = [
    // ── NOTIFICATION CHIMES ─────────────────────────────────────
    {
        id: 'apple-glass',
        name: 'Apple Glass',
        type: 'chime',
        badge: 'Chime',
        category: 'Notifications',
        description: 'Crisp, resonant glass ping with bell decay'
    },
    {
        id: 'linear-ping',
        name: 'Linear Ping',
        type: 'chime',
        badge: 'Chime',
        category: 'Notifications',
        description: 'Modern, minimalist metallic drop'
    },
    {
        id: 'crystal-ding',
        name: 'Crystal Ding',
        type: 'chime',
        badge: 'Chime',
        category: 'Notifications',
        description: 'Dual-harmonic crystal bell chime'
    },
    {
        id: 'harp-ascent',
        name: 'Ascending Harp',
        type: 'chime',
        badge: 'Chime',
        category: 'Notifications',
        description: 'Warm 4-note melodic arpeggio'
    },
    {
        id: 'zen-ripple',
        name: 'Zen Ripple',
        type: 'chime',
        badge: 'Chime',
        category: 'Notifications',
        description: 'Soft meditative droplet chord'
    },
    {
        id: 'sparkle-ping',
        name: 'Sparkle Ping',
        type: 'chime',
        badge: 'Chime',
        category: 'Notifications',
        description: 'Fast dual-frequency bright sparkle'
    },
    {
        id: 'celestial-bloom',
        name: 'Celestial Bloom',
        type: 'chime',
        badge: 'Chime',
        category: 'Notifications',
        description: 'High-pass shimmering acoustic harmonic'
    },
    {
        id: 'percussive-pop',
        name: 'Percussive Pop',
        type: 'chime',
        badge: 'Chime',
        category: 'Notifications',
        description: 'Subtle wooden tap transient'
    },

    // ── INCOMING CALL & MEETING RINGTONES ────────────────────────
    {
        id: 'marimba',
        name: 'Marimba Cadence',
        type: 'ringtone',
        badge: 'Call Ringtone',
        category: 'Calls & Meetings',
        description: 'Vibrant 4-note rhythmic marimba tune'
    },
    {
        id: 'bell-ring',
        name: 'Metallic Bell Ring',
        type: 'ringtone',
        badge: 'Call Ringtone',
        category: 'Calls & Meetings',
        description: 'Shimmering dual-frequency executive bell'
    },
    {
        id: 'executive-pulse',
        name: 'Executive Pulse',
        type: 'ringtone',
        badge: 'Call Ringtone',
        category: 'Calls & Meetings',
        description: 'Clean two-tone acoustic radar alert'
    },
    {
        id: 'horizon-melody',
        name: 'Horizon Melodic',
        type: 'ringtone',
        badge: 'Call Ringtone',
        category: 'Calls & Meetings',
        description: 'Uplifting 5-note phone cadence'
    },
    {
        id: 'vibraphone',
        name: 'Vibraphone Chime',
        type: 'ringtone',
        badge: 'Call Ringtone',
        category: 'Calls & Meetings',
        description: 'Warm jazz vibraphone tremolo chord'
    },
    {
        id: 'subtle-phone',
        name: 'Subtle Telephone',
        type: 'ringtone',
        badge: 'Call Ringtone',
        category: 'Calls & Meetings',
        description: 'Soft modern electronic double-ring'
    }
];

export const playTone = (toneId = 'apple-glass', volume = 0.45) => {
    const ctx = getAudioContext();
    if (!ctx) return;

    try {
        const now = ctx.currentTime;
        const masterGain = ctx.createGain();
        masterGain.gain.setValueAtTime(volume, now);
        masterGain.connect(ctx.destination);

        switch (toneId) {
            case 'apple-glass': {
                const freqs = [830.6, 1661.2, 2491.8];
                const weights = [0.65, 0.28, 0.12];

                freqs.forEach((freq, i) => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(freq, now);

                    gain.gain.setValueAtTime(weights[i], now);
                    gain.gain.exponentialRampToValueAtTime(0.0001, now + (i === 0 ? 0.85 : 0.45));

                    osc.connect(gain);
                    gain.connect(masterGain);
                    osc.start(now);
                    osc.stop(now + 0.9);
                });
                break;
            }

            case 'linear-ping': {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(1046.5, now);
                osc.frequency.exponentialRampToValueAtTime(880, now + 0.08);

                gain.gain.setValueAtTime(0.7, now);
                gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);

                osc.connect(gain);
                gain.connect(masterGain);
                osc.start(now);
                osc.stop(now + 0.36);
                break;
            }

            case 'crystal-ding': {
                const notes = [
                    { freq: 1046.5, time: now, decay: 0.65 },
                    { freq: 1318.5, time: now + 0.07, decay: 0.9 }
                ];

                notes.forEach(note => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(note.freq, note.time);

                    gain.gain.setValueAtTime(0.6, note.time);
                    gain.gain.exponentialRampToValueAtTime(0.0001, note.time + note.decay);

                    osc.connect(gain);
                    gain.connect(masterGain);
                    osc.start(note.time);
                    osc.stop(note.time + note.decay + 0.05);
                });
                break;
            }

            case 'harp-ascent': {
                const chord = [
                    { freq: 523.25, offset: 0, decay: 0.5 },
                    { freq: 659.25, offset: 0.07, decay: 0.55 },
                    { freq: 783.99, offset: 0.14, decay: 0.65 },
                    { freq: 1046.5, offset: 0.21, decay: 0.95 }
                ];

                chord.forEach(item => {
                    const t = now + item.offset;
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'triangle';
                    osc.frequency.setValueAtTime(item.freq, t);

                    gain.gain.setValueAtTime(0.5, t);
                    gain.gain.exponentialRampToValueAtTime(0.0001, t + item.decay);

                    osc.connect(gain);
                    gain.connect(masterGain);
                    osc.start(t);
                    osc.stop(t + item.decay + 0.05);
                });
                break;
            }

            case 'zen-ripple': {
                const chord = [440.0, 554.37, 659.25];
                chord.forEach((freq, idx) => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(freq, now + idx * 0.04);

                    gain.gain.setValueAtTime(0.35, now + idx * 0.04);
                    gain.gain.exponentialRampToValueAtTime(0.0001, now + idx * 0.04 + 0.9);

                    osc.connect(gain);
                    gain.connect(masterGain);
                    osc.start(now + idx * 0.04);
                    osc.stop(now + idx * 0.04 + 0.95);
                });
                break;
            }

            case 'sparkle-ping': {
                const sparks = [
                    { freq: 1567.98, offset: 0, decay: 0.35 },
                    { freq: 2093.00, offset: 0.06, decay: 0.6 }
                ];
                sparks.forEach(s => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(s.freq, now + s.offset);

                    gain.gain.setValueAtTime(0.45, now + s.offset);
                    gain.gain.exponentialRampToValueAtTime(0.0001, now + s.offset + s.decay);

                    osc.connect(gain);
                    gain.connect(masterGain);
                    osc.start(now + s.offset);
                    osc.stop(now + s.offset + s.decay + 0.05);
                });
                break;
            }

            case 'celestial-bloom': {
                const cluster = [1174.66, 1479.98, 1760.00];
                cluster.forEach((freq, i) => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(freq, now + i * 0.03);

                    gain.gain.setValueAtTime(0.32, now + i * 0.03);
                    gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.03 + 1.1);

                    osc.connect(gain);
                    gain.connect(masterGain);
                    osc.start(now + i * 0.03);
                    osc.stop(now + i * 0.03 + 1.15);
                });
                break;
            }

            case 'percussive-pop': {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'triangle';
                osc.frequency.setValueAtTime(750, now);
                osc.frequency.exponentialRampToValueAtTime(320, now + 0.05);

                gain.gain.setValueAtTime(0.7, now);
                gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);

                osc.connect(gain);
                gain.connect(masterGain);
                osc.start(now);
                osc.stop(now + 0.14);
                break;
            }

            // ── CALL RINGTONES ──
            case 'marimba': {
                const melody = [
                    { freq: 659.25, offset: 0, decay: 0.18 },
                    { freq: 880.00, offset: 0.10, decay: 0.18 },
                    { freq: 783.99, offset: 0.20, decay: 0.18 },
                    { freq: 1046.5, offset: 0.30, decay: 0.55 }
                ];

                melody.forEach(item => {
                    const t = now + item.offset;
                    const osc = ctx.createOscillator();
                    const overtone = ctx.createOscillator();
                    const gain = ctx.createGain();

                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(item.freq, t);
                    overtone.type = 'sine';
                    overtone.frequency.setValueAtTime(item.freq * 3, t);

                    gain.gain.setValueAtTime(0.55, t);
                    gain.gain.exponentialRampToValueAtTime(0.0001, t + item.decay);

                    osc.connect(gain);
                    overtone.connect(gain);
                    gain.connect(masterGain);

                    osc.start(t);
                    overtone.start(t);
                    osc.stop(t + item.decay + 0.05);
                    overtone.stop(t + item.decay + 0.05);
                });
                break;
            }

            case 'bell-ring': {
                const pulses = [
                    { offset: 0, decay: 0.38, vol: 0.5 },
                    { offset: 0.42, decay: 0.75, vol: 0.6 }
                ];

                pulses.forEach(pulse => {
                    const t = now + pulse.offset;
                    const osc1 = ctx.createOscillator();
                    const osc2 = ctx.createOscillator();
                    const gain = ctx.createGain();

                    osc1.type = 'sine';
                    osc1.frequency.setValueAtTime(880, t);
                    osc2.type = 'sine';
                    osc2.frequency.setValueAtTime(885.5, t);

                    gain.gain.setValueAtTime(pulse.vol, t);
                    gain.gain.exponentialRampToValueAtTime(0.0001, t + pulse.decay);

                    osc1.connect(gain);
                    osc2.connect(gain);
                    gain.connect(masterGain);

                    osc1.start(t);
                    osc2.start(t);
                    osc1.stop(t + pulse.decay + 0.05);
                    osc2.stop(t + pulse.decay + 0.05);
                });
                break;
            }

            case 'executive-pulse': {
                const tones = [
                    { freq: 587.33, offset: 0, decay: 0.22, vol: 0.45 },
                    { freq: 880.00, offset: 0.16, decay: 0.65, vol: 0.6 }
                ];

                tones.forEach(item => {
                    const t = now + item.offset;
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(item.freq, t);

                    gain.gain.setValueAtTime(item.vol, t);
                    gain.gain.exponentialRampToValueAtTime(0.0001, t + item.decay);

                    osc.connect(gain);
                    gain.connect(masterGain);
                    osc.start(t);
                    osc.stop(t + item.decay + 0.05);
                });
                break;
            }

            case 'horizon-melody': {
                const notes = [
                    { freq: 523.25, offset: 0, decay: 0.16 },
                    { freq: 587.33, offset: 0.10, decay: 0.16 },
                    { freq: 698.46, offset: 0.20, decay: 0.16 },
                    { freq: 783.99, offset: 0.30, decay: 0.22 },
                    { freq: 880.00, offset: 0.42, decay: 0.65 }
                ];
                notes.forEach(n => {
                    const t = now + n.offset;
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'triangle';
                    osc.frequency.setValueAtTime(n.freq, t);

                    gain.gain.setValueAtTime(0.48, t);
                    gain.gain.exponentialRampToValueAtTime(0.0001, t + n.decay);

                    osc.connect(gain);
                    gain.connect(masterGain);
                    osc.start(t);
                    osc.stop(t + n.decay + 0.05);
                });
                break;
            }

            case 'vibraphone': {
                const chord = [440.0, 554.37, 659.25, 880.0];
                chord.forEach(freq => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(freq, now);

                    gain.gain.setValueAtTime(0.35, now);
                    gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.2);

                    osc.connect(gain);
                    gain.connect(masterGain);
                    osc.start(now);
                    osc.stop(now + 1.25);
                });
                break;
            }

            case 'subtle-phone': {
                const rings = [0, 0.35];
                rings.forEach(offset => {
                    const t = now + offset;
                    const osc1 = ctx.createOscillator();
                    const osc2 = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc1.type = 'sine';
                    osc2.type = 'sine';
                    osc1.frequency.setValueAtTime(440, t);
                    osc2.frequency.setValueAtTime(480, t);

                    gain.gain.setValueAtTime(0.4, t);
                    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);

                    osc1.connect(gain);
                    osc2.connect(gain);
                    gain.connect(masterGain);
                    osc1.start(t);
                    osc2.start(t);
                    osc1.stop(t + 0.25);
                    osc2.stop(t + 0.25);
                });
                break;
            }

            default:
                break;
        }
    } catch {
        // Fallback or ignore
    }
};
